import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../lib/utils';

const navLinks = [
  { name: 'Forside', href: '/' },
  { name: 'Sorgsamtaler', href: '/sorgvejleder' },
  { name: 'Dødsdoula', href: '/dodsdoula' },
  { name: 'Foredrag & Workshops', href: '/foredrag-workshops' },
  { name: 'Om Asha', href: '/om-asha' },
];

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <nav
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-in-out px-6 py-4 bg-white',
        scrolled ? 'py-3 shadow-sm' : 'py-4'
      )}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <Link to="/" className="flex items-center space-x-2 group">
          <img 
            src="https://assets.auxodev.dk/assets/88d34a44-1e08-49cc-ba2b-c330133a247e/fecf0b5e-783e-45ee-a1c0-716db9fea84c.png" 
            alt="Sorgskab Logo" 
            className="h-16 w-auto object-contain"
          />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center space-x-8">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={cn(
                'relative py-1 text-sm tracking-wide transition-colors hover:text-sorg-earth',
                location.pathname === link.href ? 'text-sorg-earth font-medium' : 'text-sorg-dark/70'
              )}
            >
              {link.name}
              {location.pathname === link.href && (
                <motion.div
                  layoutId="nav-underline"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-sorg-earth rounded-full"
                  transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                />
              )}
            </Link>
          ))}
          <Link
            to="/kontakt"
            className="px-5 py-2 bg-sorg-earth text-white text-sm rounded-full hover:bg-sorg-dark transition-all duration-300 soft-shadow"
          >
            Kontakt mig
          </Link>
        </div>

        {/* Mobile Toggle */}
        <button
          className="lg:hidden text-sorg-dark"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Åbn eller luk menu"
        >
          {isOpen ? <X size={24} strokeWidth={1} /> : <Menu size={24} strokeWidth={1} />}
        </button>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="absolute top-full left-0 right-0 bg-white border-t border-sorg-beige shadow-xl lg:hidden overflow-hidden"
          >
            <div className="flex flex-col p-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={cn(
                    'flex items-center space-x-3 text-lg font-serif transition-colors',
                    location.pathname === link.href ? 'text-sorg-earth' : 'text-sorg-dark/70'
                  )}
                >
                  {location.pathname === link.href && (
                    <motion.div
                      layoutId="mobile-nav-indicator"
                      className="w-1.5 h-1.5 rounded-full bg-sorg-earth"
                    />
                  )}
                  <span>{link.name}</span>
                </Link>
              ))}
              <Link
                to="/kontakt"
                className="w-full py-3 bg-sorg-earth text-white text-center rounded-full hover:bg-sorg-dark transition-all"
              >
                Kontakt mig
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
